import React, {useState, useEffect} from "react";
import {Modal, Button, Form} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {dashboardAPI} from "../../../services/api";

export const CreateUser = ({ show, onClose, onSave }: any) => {
    const {t} = useTranslation();
    const [formState, setFormState] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        role: '',
        mobile: '',
        companyId: '',
        storeIds: [] as string[],
        storeGroupIds: [] as string[]
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<any>(null);

    useEffect(() => {
        if (show) {
            // Fetch current user to determine companyId
            dashboardAPI.getCurrentUser().then((user: any) => {
                const userData = user?.data || user;
                setCurrentUser(userData);
                if (userData?.companyId) {
                    setFormState(prev => ({ ...prev, companyId: userData.companyId }));
                }
            });
        } else {
            // Reset form when modal closes
            setFormState({
                username: '',
                email: '',
                password: '',
                confirmPassword: '',
                firstName: '',
                lastName: '',
                role: '',
                mobile: '',
                companyId: '',
                storeIds: [],
                storeGroupIds: []
            });
            setError(null);
        }
    }, [show]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormState({ ...formState, [name]: value });
    };

    const handleSubmit = async () => {
        // Validation
        setError(null);
        
        if (!formState.username || !formState.username.trim()) {
            setError(t('UserList.validation_username_required') || 'Username is required');
            return;
        }
        
        if (!formState.email || !formState.email.trim()) {
            setError(t('UserList.validation_email_required') || 'Email is required');
            return;
        }
        
        if (!formState.password || formState.password.length < 6) {
            setError(t('UserList.validation_password_min') || 'Password must be at least 6 characters');
            return;
        }
        
        if (formState.password !== formState.confirmPassword) {
            setError(t('UserList.validation_password_match') || 'Passwords do not match');
            return;
        }
        
        if (!formState.firstName || !formState.firstName.trim()) {
            setError(t('UserList.validation_firstname_required') || 'First name is required');
            return;
        }
        
        if (!formState.lastName || !formState.lastName.trim()) {
            setError(t('UserList.validation_lastname_required') || 'Last name is required');
            return;
        }
        
        if (!formState.role) {
            setError(t('UserList.validation_role_required') || 'Role is required');
            return;
        }

        setIsSubmitting(true);

        try {
            const userData: any = {
                username: formState.username.trim(),
                email: formState.email.trim(),
                password: formState.password,
                firstName: formState.firstName.trim(),
                lastName: formState.lastName.trim(),
                role: formState.role,
                mobile: formState.mobile || undefined
            };
            
            // Only include companyId if current user is General Admin
            if (currentUser?.roles?.includes('ROLE_GENERAL_ADMIN') && formState.companyId) {
                userData.companyId = formState.companyId;
            }
            
            // Store IDs and Store Group IDs will be assigned separately via assign endpoints
            await onSave(userData);
        } catch (error: any) {
            setError(error?.message || t('UserList.validation_failed') || 'Validation failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const roleOptions = [
        { value: 'ROLE_COMPANY_ADMIN', label: t('UserList.role_company_admin') || 'Company Admin' },
        { value: 'ROLE_STORE_MANAGER', label: t('UserList.role_store_manager') || 'Store Manager' },
        { value: 'ROLE_AREA_MANAGER', label: t('UserList.role_area_manager') || 'Area Manager' }
    ];

    return (
        <Modal show={show} onHide={onClose} backdrop="static" centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('UserList.create_user') || 'Create User'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.username') || 'Username'} *</Form.Label>
                        <Form.Control
                            type="text"
                            name="username"
                            value={formState.username}
                            onChange={handleChange}
                            placeholder={t('UserList.username_placeholder') || 'Enter username'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.email') || 'Email'} *</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={formState.email}
                            onChange={handleChange}
                            placeholder={t('UserList.email_placeholder') || 'Enter email'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.password') || 'Password'} *</Form.Label>
                        <Form.Control
                            type="password"
                            name="password"
                            value={formState.password}
                            onChange={handleChange}
                            placeholder={t('UserList.password_placeholder') || 'Enter password (min 6 characters)'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.confirm_password') || 'Confirm Password'} *</Form.Label>
                        <Form.Control
                            type="password"
                            name="confirmPassword"
                            value={formState.confirmPassword}
                            onChange={handleChange}
                            placeholder={t('UserList.confirm_password_placeholder') || 'Confirm password'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.first_name') || 'First Name'} *</Form.Label>
                        <Form.Control
                            type="text"
                            name="firstName"
                            value={formState.firstName}
                            onChange={handleChange}
                            placeholder={t('UserList.first_name_placeholder') || 'Enter first name'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.last_name') || 'Last Name'} *</Form.Label>
                        <Form.Control
                            type="text"
                            name="lastName"
                            value={formState.lastName}
                            onChange={handleChange}
                            placeholder={t('UserList.last_name_placeholder') || 'Enter last name'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.role') || 'Role'} *</Form.Label>
                        <Form.Select
                            name="role"
                            value={formState.role}
                            onChange={handleChange}
                        >
                            <option value="">{t('UserList.select_role') || 'Select role'}</option>
                            {roleOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.mobile') || 'Mobile'}</Form.Label>
                        <Form.Control
                            type="text"
                            name="mobile"
                            value={formState.mobile}
                            onChange={handleChange}
                            placeholder={t('UserList.mobile_placeholder') || 'Enter mobile number'}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="dark" onClick={onClose} disabled={isSubmitting}>
                    {t('RuleList.cancel') || 'Cancel'}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? (t('RuleList.saving') || 'Saving...') : (t('RuleList.save') || 'Save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

